/**
 * Client calls for the Team page — the ops dashboard's operator accounts — on the shared
 * {@link opsClient}. Every write carries the operator's typed reason, which the API stores in
 * `OperatorAuditLog` before the change happens.
 */

import type {
  OperatorDto,
  OperatorOneTimePasswordDto,
} from '../../../../libs/api-client/src/generated/models';
import { opsClient } from './ops-api';

export type {
  OperatorDto as Operator,
  OperatorOneTimePasswordDto as OperatorOneTimePassword,
} from '../../../../libs/api-client/src/generated/models';

/** Every account, disabled ones included. */
export async function listOperators(): Promise<OperatorDto[]> {
  const { data } = await opsClient().listOperators();
  return data.operators;
}

/** Creates an account; the answer carries its one-time password, shown once. */
export async function createOperator(input: {
  username: string;
  displayName: string;
  email: string;
  reason: string;
}): Promise<OperatorOneTimePasswordDto> {
  const { data } = await opsClient().createOperator({
    createOperatorDto: {
      username: input.username,
      displayName: input.displayName,
      ...(input.email.trim() ? { email: input.email.trim() } : {}),
      reason: input.reason,
    },
  });
  return data;
}

/** Edits name and email. An empty email removes it. */
export async function updateOperator(
  username: string,
  input: { displayName: string; email: string; reason: string },
): Promise<OperatorDto> {
  const { data } = await opsClient().updateOperator({
    username,
    updateOperatorDto: {
      displayName: input.displayName,
      email: input.email.trim(),
      reason: input.reason,
    },
  });
  return data;
}

/** Replaces the password with a one-time one and signs the account out everywhere. */
export async function resetOperatorPassword(
  username: string,
  reason: string,
): Promise<OperatorOneTimePasswordDto> {
  const { data } = await opsClient().resetOperatorPassword({
    username,
    operatorReasonDto: { reason },
  });
  return data;
}

/** Disables an account and ends its sessions. */
export async function disableOperator(
  username: string,
  reason: string,
): Promise<OperatorDto> {
  const { data } = await opsClient().disableOperator({
    username,
    operatorReasonDto: { reason },
  });
  return data;
}

/** Re-enables a disabled account. */
export async function enableOperator(
  username: string,
  reason: string,
): Promise<OperatorDto> {
  const { data } = await opsClient().enableOperator({
    username,
    operatorReasonDto: { reason },
  });
  return data;
}
